import { memo } from "react";
import type { IntelligenceItem, FallbackTier, NewsDigestTopline } from "@kmitl/shared";
import { uiStrings } from "@kmitl/shared";
import { safeUrl } from "../lib/safeUrl";
import { PanelHeader } from "./PanelHeader";
import { RefMark } from "./Icons";

/** KMITL's official page on Facebook. */
const KMITL_FB_URL = "https://www.facebook.com/kmitlofficial/";

function ago(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const ms = Date.now() - t;
  const m = Math.round(ms / 60000);
  if (m < 1) return "NOW";
  if (m < 60) return `${m}M`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}H`;
  return `${Math.round(h / 24)}D`;
}

function scoreColor(score: number): string {
  if (score >= 1000) return "var(--bad)";
  if (score >= 500)  return "var(--warn)";
  return "var(--text-3)";
}

interface Props {
  items: IntelligenceItem[];
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
  onRefresh?: () => void;
  /** Set to true (via useFeed fallbackTier === "unavailable") to show error state. */
  unavailable?: boolean;
  locale?: "en" | "th" | "zh";
  /** Top headlines from /api/news/digest — highest-scored items over the last 7 days. */
  digestToplines?: NewsDigestTopline[];
  /** Total articles archived in the digest window (for the eyebrow label). */
  digestTotal?: number;
}

export const NewsDesk = memo(function NewsDesk({ items, loading, ageMinutes, fallbackTier, onRefresh, unavailable, locale = "en", digestToplines, digestTotal }: Props) {
  const s = uiStrings(locale);
  const title = `${s.intelFeed} // CAMPUS`;

  if (loading && items.length === 0) {
    return (
      <div className="col">
        <PanelHeader title={title} source="rss·google-news" />
        <div className="skeleton" style={{ height: 10, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 10, width: "80%", marginTop: 6 }} />
        <div className="skeleton" style={{ height: 10, width: "60%", marginTop: 6 }} />
      </div>
    );
  }

  if (unavailable && items.length === 0) {
    return (
      <div className="col">
        <PanelHeader title={title} source="rss·google-news" fallbackTier="unavailable" />
        <div className="caption" style={{ color: "var(--text-3)", marginTop: 8 }}>
          {s.unavailable}.
        </div>
      </div>
    );
  }

  return (
    <div className="col">
      <PanelHeader
        title={title}
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="rss·google-news"
        actions={
          <span className="news-count-cluster">
            <span className="news-count mono">{String(items.length).padStart(3, "0")}</span>
            {/* The link to KMITL's official page used to live in a second
                panel below this one, which rendered the same news items
                under an identical header — two stacks of the same feed in a
                280 px rail. The link is the only thing that panel had that
                this one did not, so it lives here now. */}
            <a
              href={KMITL_FB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="news-fb-link mono"
              title="KMITL official page on Facebook"
            >
              FB ↗
            </a>
            {onRefresh && (
              <button
                type="button"
                className="news-refresh mono"
                onClick={onRefresh}
                disabled={loading}
                title={ageMinutes != null ? `Refreshed ${ageMinutes}m ago — click to refresh` : "Refresh"}
                aria-label="Refresh news"
              >
                {loading ? "…" : "↻"}
              </button>
            )}
          </span>
        }
      />
      {digestToplines && digestToplines.length > 0 && (
        <div className="news-digest-block">
          <div className="eyebrow mono news-digest-eyebrow">
            THIS WEEK
            {digestTotal != null && digestTotal > 0 && (
              <span className="caption" style={{ color: "var(--text-3)" }}> · {digestTotal} articles</span>
            )}
          </div>
          {digestToplines.map((dl) => (
            <a
              key={dl.url}
              href={safeUrl(dl.url) ?? "#"}
              target="_blank"
              rel="noreferrer noopener"
              className="news-item news-item--digest"
            >
              <div className="news-header">
                <span className="news-ref"><RefMark size={11} /></span>
                <span className="news-score mono" style={{ color: scoreColor(dl.score) }}>
                  REL·{dl.score}
                </span>
              </div>
              <div className="title">{dl.title}</div>
              <div className="meta">
                <span>{dl.source.toUpperCase()}</span>
                <span>·</span>
                <span className="mono">{ago(dl.firstSeenAt)}</span>
              </div>
            </a>
          ))}
          <div className="news-digest-sep" />
        </div>
      )}
      <div>
        {items.slice(0, 12).map((it, i) => (
          <a key={it.id} href={safeUrl(it.sourceUrl) ?? "#"} target="_blank" rel="noreferrer noopener" className="news-item">
            <div className="news-header">
              <span className="news-ref mono">[{String(i + 1).padStart(3, "0")}]</span>
              <span className="news-score mono" style={{ color: scoreColor(it.score) }}>
                REL·{it.score}
              </span>
            </div>
            <div className="title">{it.title}</div>
            <div className="meta">
              <span>{it.source.toUpperCase()}</span>
              <span>·</span>
              <span className="mono">{ago(it.publishedAt)}</span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
});
